import type { ModelCatalogEntry, ResponseMetrics } from './types';

/**
 * Base class for typed HTTP errors thrown by the API client.
 * Carries the HTTP status code and a human-readable message sourced from
 * the server's `{ error: string }` body when available.
 */
export class ApiError extends Error {
  public readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** 401 Unauthorized - the caller should clear tokens and redirect to sign-in. */
export class UnauthorizedError extends ApiError {
  constructor(message = 'Unauthorized') {
    super(401, message);
    this.name = 'UnauthorizedError';
  }
}

/** 400 Bad Request - client-side validation failure (e.g. unknown modelId, empty prompt). */
export class BadRequestError extends ApiError {
  constructor(message: string) {
    super(400, message);
    this.name = 'BadRequestError';
  }
}

/** 502 Bad Gateway - upstream (Bedrock) invocation failed. */
export class UpstreamError extends ApiError {
  constructor(message: string) {
    super(502, message);
    this.name = 'UpstreamError';
  }
}

/** 504 Gateway Timeout - the upstream (Bedrock) stream exceeded the server's 30 s budget. */
export class RequestTimeoutError extends ApiError {
  constructor(message: string) {
    super(504, message);
    this.name = 'RequestTimeoutError';
  }
}

/**
 * Attempt to parse the response body as JSON. Returns an empty object if the
 * body is absent or not valid JSON so callers can safely read fields like `.error`.
 */
async function safeJson(res: Response): Promise<Record<string, unknown>> {
  try {
    const data = await res.json();
    return data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/**
 * Map a non-2xx response + parsed body to the appropriate typed error.
 * Unknown statuses produce a generic `ApiError` so callers can still branch on `.status`.
 */
function buildError(status: number, body: Record<string, unknown>): ApiError {
  const bodyMessage = typeof body.error === 'string' ? body.error : '';

  switch (status) {
    case 400:
      return new BadRequestError(bodyMessage || 'Bad request');
    case 401:
      return new UnauthorizedError(bodyMessage || 'Unauthorized');
    case 502:
      return new UpstreamError(bodyMessage || 'Model invocation failed');
    case 504:
      return new RequestTimeoutError(bodyMessage || 'Request timed out');
    default:
      return new ApiError(status, bodyMessage || `Request failed with status ${status}`);
  }
}

/**
 * Fetch the model catalog from the backend.
 * Throws a typed `ApiError` subclass on any non-2xx response so callers can
 * branch on the error type.
 */
export async function fetchModels(accessToken: string): Promise<ModelCatalogEntry[]> {
  const res = await fetch('/api/models', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!res.ok) {
    throw buildError(res.status, await safeJson(res));
  }

  const data = (await res.json()) as { models?: ModelCatalogEntry[] };
  return data.models ?? [];
}

/**
 * Send a chat prompt to the backend for the given model.
 * Returns the generated text and the per-response metrics block.
 * Throws a typed `ApiError` subclass on non-2xx so `ChatPage` can branch on
 * 401 (sign-out), 400 (validation), 502 (upstream), 504 (timeout), etc.
 */
export async function postChat(
  accessToken: string,
  prompt: string,
  modelId: string,
): Promise<{ response: string; metrics?: ResponseMetrics }> {
  const res = await fetch('/api/chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ prompt, modelId }),
  });

  if (!res.ok) {
    throw buildError(res.status, await safeJson(res));
  }

  return (await res.json()) as { response: string; metrics?: ResponseMetrics };
}
